import {
  guardEmptyArray,
  guardEmptyString,
  guardMaxLengthString,
  guardUnspecified
} from '@portal/utils/util-guards';
import { componentFactoryOf } from 'vue-tsx-support';

import { ImageDecoding, ImageLoading } from '@jtnews/shared/images';

import { injectStylesMixin } from '../../../mixins/inject-styles';
import { JtnUiAvatar } from '../../jtn-ui-avatar';
import { JtnUiImage } from '../../jtn-ui-image';
import { JtnUiTypography } from '../../jtn-ui-typography';

import styles from './jtn-ui-record-opinion-authors.styles.scss?module';

interface Events {
  onClickedAuthor: {
    event: Event;
    url: string;
    id: number;
    name: string;
  };
}

type Author = {
  id: string;
  url?: string;
  name: string;
  targetUrl?: string;
  jobPosition: string;
  image?: {
    width: number;
    height: number;
    img: ImageVM;
    sources: Source[];
  };
};

type Source = {
  srcset: string;
  type?: string;
  media?: string;
};

type ImageVM = {
  src: string;
  srcset?: string | undefined;
  sizes?: string | undefined;
  loading?: ImageLoading | undefined;
  decoding?: ImageDecoding | undefined;
};

const DEFAULT_AVATAR_SIZE = 90;

export default componentFactoryOf<Events>()
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'JtnUiRecordOpinionAuthors',
    props: {
      authors: {
        type: Array as () => Author[],
        default() {
          return [];
        }
      },
      isHeader: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      mainAuthor(): Author {
        return this.authors[0];
      },
      secondaryAuthors() {
        if (this.isHeader) {
          return [];
        }

        return this.authors.slice(1);
      }
    },
    methods: {
      cutText(text: string): string {
        const MAX_LENGTH = 20;
        return guardMaxLengthString(text, MAX_LENGTH)
          ? text
          : `${text.slice(0, MAX_LENGTH)}...`;
      },
      getAuthorBlock(author: Author, type: 'main' | 'secondary' = 'secondary') {
        if (author) {
          const COMPONENT_DATA = author.url
            ? {
                attrs: {
                  tag: 'a',
                  href: author.url,
                  target: author.targetUrl
                }
              }
            : {
                attrs: {
                  tag: 'span'
                }
              };

          return (
            <div
              class={styles.author}
              itemprop="author"
              itemscope
              itemtype="http://schema.org/Person"
            >
              <JtnUiTypography
                {...COMPONENT_DATA}
                type="font-vijet-type-1"
                class={[
                  styles.authorCard,
                  type === 'secondary' ? '' : styles.mainAuthor,
                  this.isHeader ? styles.inHeader : ''
                ]}
                nativeOnClick={(event: Event) => {
                  this.$emit('clickedAuthor', {
                    event,
                    url: author.url,
                    id: this.mainAuthor.id,
                    name: this.mainAuthor.name
                  });
                }}
              >
                <JtnUiAvatar
                  type="author"
                  size={author.image?.width ?? DEFAULT_AVATAR_SIZE}
                  isSquare={true}
                >
                  {guardUnspecified(author.image) &&
                    guardEmptyString(author.image.img.src) && (
                      <JtnUiImage
                        slot="photo"
                        src={author.image.img.src}
                        sources={author.image.sources}
                        width={author.image.width}
                        height={author.image.height}
                        alt="Фото автора"
                      />
                    )}
                </JtnUiAvatar>
                <div class={styles.authorDescription}>
                  <p class={styles.authorName} itemprop="name">
                    {author.name}
                  </p>
                  <p class={styles.authorPosition} itemprop="position">
                    {type === 'main'
                      ? author.jobPosition
                      : this.cutText(author.jobPosition)}
                  </p>
                </div>
              </JtnUiTypography>
            </div>
          );
        }
      },
      getOpinionRemark(screen: 'mobile' | 'desktop' | 'all') {
        return (
          !this.isHeader && (
            <p class={[styles.opinionsPolicy, styles[screen]]}>
              Мнение автора может не совпадать с&nbsp;мнением редакции
            </p>
          )
        );
      }
    },
    render() {
      const attributes = {
        attrs: this.$attrs,
        on: this.$listeners
      };

      return (
        <section class={styles.recordAuthorsOpinion} {...attributes}>
          <div class={styles.authorsContainer}>
            {this.getAuthorBlock(this.mainAuthor, 'main')}
            {this.getOpinionRemark(
              guardEmptyArray(this.secondaryAuthors) ? 'desktop' : 'all'
            )}
          </div>
          {guardEmptyArray(this.secondaryAuthors) && [
            <ul class={styles.authorsList}>
              {this.secondaryAuthors.map(author => (
                <li key={author.id} class={styles.authorsItem}>
                  {this.getAuthorBlock(author)}
                </li>
              ))}
            </ul>,
            this.getOpinionRemark('mobile')
          ]}
        </section>
      );
    }
  });
